import React, { useMemo } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

export interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isDarkMode?: boolean;
  minHeight?: string;
  label?: string;
  error?: string;
  className?: string;
  fullToolbar?: boolean; // If true, show all controls; if false, show limited controls
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter text...',
  isDarkMode = true,
  minHeight = '120px',
  label,
  error,
  className = '',
  fullToolbar = false,
}) => {
  // Quill modules configuration
  const modules = useMemo(() => ({
    toolbar: fullToolbar ? [
      // Full toolbar for brand profile
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'font': [] }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'list': 'ordered'}],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'align': [] }],
      ['link', 'image', 'video'],
      ['blockquote', 'code-block'],
      ['clean']
    ] : [
      // Limited toolbar for collections
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'underline'],
      [{ 'list': 'ordered'}],
      ['blockquote'],
    ],
  }), [fullToolbar]);

  const formats = useMemo(() => 
    fullToolbar ? [
      'header', 'font', 'size',
      'bold', 'italic', 'underline', 'strike',
      'color', 'background',
      'script',
      'list',
      'indent',
      'direction', 'align',
      'link', 'image', 'video',
      'blockquote', 'code-block'
    ] : [
      'header',
      'bold', 'underline',
      'list',
      'blockquote'
    ]
  , [fullToolbar]);

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
          {label}
        </label>
      )}
      
      <div
        className={`rounded-xl overflow-hidden border transition-all ${
          error
            ? 'border-red-500'
            : 'border-gray-200 dark:border-white/10'
        }`}
      >
        <ReactQuill
          theme="snow"
          value={value || ''}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          className={`rich-text-editor ${isDarkMode ? 'dark-mode' : 'light-mode'}`}
          style={{ minHeight }}
        />
      </div>

      {error && (
        <p className="mt-1.5 text-sm text-red-500">{error}</p>
      )}

      <style>{`
        /* Dark Mode Styles */
        .rich-text-editor.dark-mode .ql-toolbar {
          background: rgba(255, 255, 255, 0.05);
          border: none;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .rich-text-editor.dark-mode .ql-container {
          background: rgba(255, 255, 255, 0.05);
          border: none;
          color: #e5e7eb;
        }
        
        .rich-text-editor.dark-mode .ql-editor {
          color: #e5e7eb;
          min-height: ${minHeight};
        }
        
        .rich-text-editor.dark-mode .ql-editor.ql-blank::before {
          color: #6b7280;
          font-style: normal;
        }
        
        .rich-text-editor.dark-mode .ql-stroke {
          stroke: #9ca3af;
        }
        
        .rich-text-editor.dark-mode .ql-fill {
          fill: #9ca3af;
        }
        
        .rich-text-editor.dark-mode .ql-picker-label {
          color: #9ca3af;
        }
        
        .rich-text-editor.dark-mode .ql-toolbar button:hover,
        .rich-text-editor.dark-mode .ql-toolbar button.ql-active {
          background: rgba(110, 21, 236, 0.2);
        }
        
        .rich-text-editor.dark-mode .ql-toolbar button:hover .ql-stroke,
        .rich-text-editor.dark-mode .ql-toolbar button.ql-active .ql-stroke {
          stroke: #6e15ec;
        }
        
        .rich-text-editor.dark-mode .ql-toolbar button:hover .ql-fill,
        .rich-text-editor.dark-mode .ql-toolbar button.ql-active .ql-fill {
          fill: #6e15ec;
        }

        /* Light Mode Styles */
        .rich-text-editor.light-mode .ql-toolbar {
          background: #ffffff;
          border: none;
          border-bottom: 1px solid #e5e7eb;
        }
        
        .rich-text-editor.light-mode .ql-container {
          background: #ffffff;
          border: none;
        }
        
        .rich-text-editor.light-mode .ql-editor {
          color: #111827;
          min-height: ${minHeight};
        }
        
        .rich-text-editor.light-mode .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: normal;
        }
        
        .rich-text-editor.light-mode .ql-toolbar button:hover,
        .rich-text-editor.light-mode .ql-toolbar button.ql-active {
          background: rgba(110, 21, 236, 0.1);
        }
        
        .rich-text-editor.light-mode .ql-toolbar button:hover .ql-stroke,
        .rich-text-editor.light-mode .ql-toolbar button.ql-active .ql-stroke {
          stroke: #6e15ec;
        }
        
        .rich-text-editor.light-mode .ql-toolbar button:hover .ql-fill,
        .rich-text-editor.light-mode .ql-toolbar button.ql-active .ql-fill {
          fill: #6e15ec;
        }

        /* Common Styles */
        .rich-text-editor .ql-toolbar {
          padding: 10px;
        }
        
        .rich-text-editor .ql-container {
          font-size: 14px;
          line-height: 1.6;
        }
        
        .rich-text-editor .ql-editor {
          padding: 16px;
        }
      `}</style>
    </div>
  );
};

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
